import type { Game } from '@/types/Types'
import { toRaw } from 'vue'
import { getDb } from './offlineDb'

const STORE_NAME = 'gameState'

/**
 * Produces a structured-clone-safe copy of a Game for IndexedDB.
 *
 * IndexedDB serialises via the structured-clone algorithm, which throws
 * `DataCloneError` on values it cannot clone: Firestore class instances
 * (`Timestamp`, the `FieldValue` sentinel returned by `serverTimestamp()`,
 * `DocumentReference`) and Vue reactive proxies. A live `Game` from the store
 * carries both — `gameStartedDate` is a Firestore sentinel/Timestamp, and the
 * object itself is a Pinia reactive proxy.
 *
 * `toRaw` unwraps the proxy; the JSON round-trip strips every value down to
 * plain data. This is lossless for our purposes: the offline cache is only read
 * back to resume gameplay (players, scores, challenge index), and
 * `gameStartedDate` has no `toDate()` consumer on the game path — it is written
 * once at creation and never re-sent (`updateGame` only writes
 * `currentChallengeIndex` and `players`).
 */
function toCacheableGame(game: Game): Game {
  return JSON.parse(JSON.stringify(toRaw(game)))
}

export async function saveGameState(game: Game) {
  const db = await getDb()
  await db.put(STORE_NAME, toCacheableGame(game))
}

export async function loadGameState(gameId: string): Promise<Game | undefined> {
  const db = await getDb()
  return db.get(STORE_NAME, gameId)
}

export async function clearGameState(gameId: string) {
  const db = await getDb()
  await db.delete(STORE_NAME, gameId)
}
